"use client";

/**
 * Client body for the Teams integration page. Lives apart from `page.tsx`
 * (which stays a server component for `metadata`) so it can call `useT()` and
 * follow the visitor's locale, exactly like the homepage. All copy comes from
 * the `intgTeams` namespace; structure (icons, hrefs, capability status) stays
 * here.
 */

import {
  MessageSquare,
  ShieldCheck,
  ListChecks,
  Inbox,
  Lock,
  History,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  UseCaseHero,
  Section,
  SectionHeader,
  FeatureGrid,
  CapabilityGrid,
  Faq,
  SecurityNote,
  RelatedLinks,
  CtaBanner,
} from "@/components/marketing/marketing-kit";
import { useT } from "@/i18n";

export function TeamsContent() {
  const t = useT();
  const k = (s: string) => `intgTeams.${s}`;

  const features = [
    { key: "intake", icon: <Inbox size={18} /> },
    { key: "questions", icon: <ListChecks size={18} /> },
    { key: "approvals", icon: <ShieldCheck size={18} /> },
    { key: "sync", icon: <History size={18} /> },
    { key: "identities", icon: <Lock size={18} /> },
    { key: "replies", icon: <MessageSquare size={18} /> },
  ].map((f) => ({
    icon: f.icon,
    title: t(k(`features.${f.key}.title`)),
    body: t(k(`features.${f.key}.body`)),
  }));

  const capabilities = [
    { key: "receive", status: "supported" as const },
    { key: "answering", status: "supported" as const },
    { key: "cards", status: "supported" as const },
    { key: "post", status: "conditional" as const },
    { key: "singleTenant", status: "conditional" as const },
    { key: "identity", status: "conditional" as const },
    { key: "greenStatus", status: "unavailable" as const },
  ].map((c) => ({
    name: t(k(`capabilities.${c.key}.name`)),
    status: c.status,
    body: t(k(`capabilities.${c.key}.body`)),
  }));

  const faq = ["q1", "q2", "q3", "q4", "q5", "q6"].map((q) => ({
    q: t(k(`faq.${q}.q`)),
    a: t(k(`faq.${q}.a`)),
  }));

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "/demo", label: t(k("hero.primaryCta")) }}
        secondary={{ href: "/use-cases/slack-jira-ticket-questions", label: t(k("hero.secondaryCta")) }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      <Section>
        <SectionHeader eyebrow={t(k("what.eyebrow"))} title={t(k("what.title"))} />
        <FeatureGrid features={features} />
      </Section>

      <Section muted>
        <SectionHeader
          eyebrow={t(k("capabilities.eyebrow"))}
          title={t(k("capabilities.title"))}
          description={t(k("capabilities.description"))}
        />
        <CapabilityGrid
          capabilities={capabilities}
          statusLabels={{
            supported: t("mk.cap.supported"),
            conditional: t("mk.cap.conditional"),
            unavailable: t("mk.cap.unavailable"),
          }}
        />
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("failures.eyebrow"))} title={t(k("failures.title"))} />
        <SecurityNote icon={<History size={18} />} title={t(k("failures.noteTitle"))}>
          {t(k("failures.noteBody"))}
        </SecurityNote>
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("faq.eyebrow"))} title={t(k("faq.title"))} />
        <Faq items={faq} />
      </Section>

      <Section>
        <RelatedLinks
          title={t("mk.relatedTitle")}
          links={[
            {
              href: "/integrations/slack",
              label: t(k("related.slack.label")),
              description: t(k("related.slack.description")),
            },
            {
              href: "/use-cases/slack-jira-ticket-questions",
              label: t(k("related.questions.label")),
              description: t(k("related.questions.description")),
            },
            {
              href: "/use-cases/ai-agent-approval-ledger",
              label: t(k("related.ledger.label")),
              description: t(k("related.ledger.description")),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/demo", label: t(k("cta.primary")) }}
        secondary={{ href: "/contact", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
